import {useEffect, useState} from "react";

import {useViewer} from "../context/ViewerContext";
import {evaluateFlag} from "../utils/flags";

/**
 * LaunchDarkly JSON flag driving the revamped profile listing in the main app
 * (see ProfileListingVirtualizedConfigSchema in @buildwithflux/app-flags-schema).
 * Its listing and card controls configure the search results here.
 */
const FLAG_KEY = "profile-listing-virtualized";

export interface SearchListingConfig {
    /** Full-span virtualized results listing replacing paged navigation. */
    virtualization: boolean;
    /** List-aligned browser scrollbar; only effective with virtualization. */
    customScrollbar: boolean;
    /** Shared fixed-height project cards replacing the legacy search cards. */
    cardUnification: boolean;
}

const defaultConfig: SearchListingConfig = {
    virtualization: false,
    customScrollbar: false,
    cardUnification: false,
};

function normalize(value: unknown): SearchListingConfig {
    if (typeof value !== "object" || value === null) {
        return defaultConfig;
    }
    const flag = value as {
        virtualization?: unknown;
        customScrollbar?: unknown;
        searchCardUnification?: unknown;
    };
    const virtualization = flag.virtualization === true;
    return {
        virtualization,
        customScrollbar: virtualization && flag.customScrollbar === true,
        cardUnification: flag.searchCardUnification === true,
    };
}

/**
 * The viewer's search-listing configuration. Defaults to the classic paged
 * listing and legacy cards. The flag is evaluated once after auth resolves.
 */
export function useSearchListingConfig(): SearchListingConfig {
    const viewer = useViewer();
    const {uid, email, handle, authResolved} = viewer;
    const identityKey = `${uid ?? ""}\0${email ?? ""}\0${handle ?? ""}`;
    const [config, setConfig] = useState(defaultConfig);
    // Reset during render on identity change (not in the effect), so a prior
    // viewer's configuration is never observable for even one committed render.
    const [lastIdentityKey, setLastIdentityKey] = useState(identityKey);
    if (identityKey !== lastIdentityKey) {
        setLastIdentityKey(identityKey);
        setConfig(defaultConfig);
    }

    useEffect(() => {
        if (!authResolved) {
            return undefined;
        }
        let cancelled = false;
        void evaluateFlag(FLAG_KEY, viewer)
            .then((value) => {
                if (!cancelled) {
                    setConfig(normalize(value));
                }
            })
            .catch(() => {
                // Keep the classic listing when evaluation fails
            });
        return () => {
            cancelled = true;
        };
    }, [authResolved, viewer]);

    return config;
}
